import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { ApiTeamLink } from "@/types/team.types.ts";
import { Button, Input, useModals, Modal, ModalProps, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const rUrl = /^https?:\/\/\S+$/i;

interface TeamLinkEditModalProps extends ModalProps {
  link: ApiTeamLink | undefined;
  onSubmit: (link: ApiTeamLink) => void;
  onDelete: () => void;
}

export default function TeamLinkEditModal({
  link,
  onSubmit,
  onDelete,
  ...props
}: TeamLinkEditModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  const linkSchema = z.object({
    title: z.string({ error: t("errors:field_required") }).min(1, t("errors:field_required")),
    url: z
      .string({ error: t("errors:field_required") })
      .min(1, t("errors:field_required"))
      .regex(rUrl, t("errors:field_url_invalid")),
  });

  type LinkSchema = z.infer<typeof linkSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LinkSchema>({
    mode: "onSubmit",
    resolver: zodResolver(linkSchema),
    defaultValues: useMemo(
      () => ({
        title: link?.title,
        url: link?.url,
      }),
      [link]
    ),
  });

  const handleSave = handleSubmit((formData) => {
    onSubmit({ title: formData.title, url: formData.url });
    close(id);
  });

  return (
    <>
      <Modal
        {...props}
        title={link ? t("core:edit_link") : t("core:add_link")}
        withCloser
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button type={"submit"} size={"lg"} onClick={handleSave}>
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Input
            required={true}
            label={t("common:title")}
            autoFocus={true}
            type="text"
            {...register("title")}
            error={errors.title?.message}
          />

          <Input
            required={true}
            label={t("common:url")}
            type="text"
            {...register("url")}
            error={errors.url?.message}
          />

          {link && (
            <DeleteListItem
              title={t("core:delete_link_question")}
              onDelete={() => {
                onDelete();
                close(id);
              }}
            />
          )}
        </form>
      </Modal>
    </>
  );
}
